<template>
  <div class="account-mapping-page-header__outer-container">
    <div>
      <span class="account-mapping-page-header__title">{{
        I18n.t('account_mapping_list.account_mapping')
      }}</span>
    </div>
    <div class="account-mapping-page-header__button-container">
      <a class="sf-btn" :href="manualMappingPath">{{
        I18n.t('account_mapping_list.ai_mapping_cta.map_manually_label')
      }}</a>
      <a
        class="sf-btn sf-btn-success"
        :class="{ 'sf-disabled': saving || !getHasUnsavedChanges }"
        :data-track="isInAiMode"
        @click="handleSaveClick"
      >
        <div v-if="saving" class="sf-spinner sf-inline-block" />
        {{ I18n.t('account_mapping_list.save') }}
      </a>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'

import { getSafeJSONValue, setJSONValue } from '@app/utils/local-storage'
import { ON_LOAD_BANNERS_KEY } from '@app/application/layout/sections/sf-flash-manager/constants'
import { useBannerManager } from '@app/shared/pinia/banner-manager'
import { bannerVariants } from '@app/shared/types/banner'
import { useCommon } from '@app/application/account-mapping/store/common'
import { useLocale } from '@app/application/account-mapping/store/locale'

export default {
  props: {
    authenticityToken: {
      type: String,
      required: true,
    },
    saveMethod: {
      type: String,
      required: true,
    },
    saveUrl: {
      type: String,
      required: true,
    },
    saveBatchSize: {
      type: Number,
      required: true,
    },
    aiMode: {
      type: Boolean,
    },
  },
  data() {
    return {
      saving: false,
    }
  },
  computed: {
    ...mapState(useCommon, ['getHasUnsavedChanges']),
    ...mapState(useLocale, ['supportedLocales']),
    manualMappingPath() {
      return Silverfin.Routes.edit_mapping_accounts_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId
      )
    },
    isInAiMode() {
      return this.aiMode ? 'ai-mapping-save' : ''
    },
  },
  mounted() {
    const banners = getSafeJSONValue(ON_LOAD_BANNERS_KEY)
    if (banners?.length) {
      this.raiseBanner({
        id: `account-mapping-save-banner`,
        variant: bannerVariants.danger,
        description: banners,
        title: I18n.t('account_mapping_list.save_error_title'),
        showClose: false,
      })

      setJSONValue(ON_LOAD_BANNERS_KEY, null)
    }
  },
  methods: {
    ...mapActions(useCommon, ['saveMappingBatched']),
    ...mapActions(useBannerManager, ['raiseBanner']),
    handleSaveClick: async function () {
      if (!this.getHasUnsavedChanges) {
        return
      }

      this.saving = true

      const params = {
        url: this.saveUrl,
        method: this.saveMethod,
        authenticityToken: this.authenticityToken,
        saveBatchSize: this.saveBatchSize,
        supportedLocales: this.supportedLocales,
      }

      const response = await this.saveMappingBatched(params)
      const { errors, nextPath } = response
      // Batched saving might have some succesful requests, some failures.
      // Simplest way to "refresh" the state of the AccountMapping table is to redirect/reload the page.
      // At the same time we want to show the errors from this save operation. As such,
      // set the errors in LocalStorage to be displayed as banners/flash on next page load.
      setJSONValue(ON_LOAD_BANNERS_KEY, errors)

      if (nextPath) {
        Turbolinks.visit(nextPath, { action: 'replace' })
      }

      this.saving = false
    },
  },
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.account-mapping-page-header {
  &__outer-container {
    @include background-color('white');

    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: space(3);
    border-bottom: 1px solid -color('snow', 'dark');
  }

  &__title {
    @include color('text');
    @include font-size('heading', 'm');
    @include font-weight('medium');

    line-height: 1.33;
  }

  &__button-container {
    display: flex;
    gap: 15px;
  }
}
</style>
